import { Theme, alpha, makeStyles } from '@material-ui/core';
import Switch, { SwitchProps } from '@material-ui/core/Switch';

import * as React from 'react';

export type DSBasicSwitchProps = SwitchProps;

export const DSBasicSwitch: React.FC<DSBasicSwitchProps> = props => <Switch {...props} />;
DSBasicSwitch.displayName = 'DSBasicSwitch';

export interface DSSwitchStyleProps {
    onLabel: string;
    offLabel: string;
}

export interface DSSwitchProps extends Omit<DSBasicSwitchProps, 'color'>, Partial<DSSwitchStyleProps> {}

function getLargestString(...values: string[]) {
    return values.reduce((collector, current) => (current.length > collector.length ? current : collector));
}

const useDSSwitchStyles = makeStyles<Theme, DSSwitchStyleProps>((theme: Theme) => ({
    checked: {
        '& + $thumb': {
            backgroundColor: '#fff',
        },
        '& + $track': {
            '&:after': {
                opacity: 0,
            },
            '&:before': {
                opacity: 1,
            },
            background: theme.palette.success.main,
        },
        '& .MuiSwitch-input': {
            left: 'initial',
            right: 0,
        },
        '&$switchBase': {
            '&:hover': {
                backgroundColor: alpha(theme.palette.success.dark, 0.1),
            },
            color: theme.palette.success.dark,
            left: '100%',
            position: 'absolute',
            right: 0,
            transform: 'translateX(-100%) translateY(-50%) !important',
        },
    },
    disabled: {
        '& + $track': {
            background: theme.palette.grey[500],
        },
    },
    input: {
        '&:before': {
            content: (props: DSSwitchStyleProps) => `"${getLargestString(props.offLabel, props.onLabel, 'on')}"`,
            display: 'inline-block',
            opacity: 0,
            paddingLeft: 46,
            position: 'relative',
        },
        margin: '0 !important',
        outline: 'none !important',
        outlineOffset: '0 !important',
        width: '100000%',
    },
    root: {
        '&:before': {
            content: (props: DSSwitchStyleProps) => `"${getLargestString(props.offLabel, props.onLabel, 'on')}"`,
            display: 'inline-block',
            opacity: 0,
            paddingLeft: 46,
            position: 'relative',
        },
        height: 34,
        margin: 1,
        padding: 0,
        width: 'auto',
    },
    switchBase: {
        '&$checked': {
            transform: 'translateX(-100%) translateY(-50%) !important',
        },
        '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.1),
        },
        color: '#ff6a00',
        padding: 5,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 36,
    },
    thumb: {
        backgroundColor: '#fff',
        height: 26,
        width: 26,
    },
    track: {
        '&:after': {
            content: (props: DSSwitchStyleProps) => `"${props.offLabel}"`,
            left: '36px',
        },
        '&:before': {
            content: (props: DSSwitchStyleProps) => `"${props.onLabel}"`,
            opacity: 0,
            right: '36px',
        },
        '&:before, &:after': {
            color: '#fff',
            display: 'inline-block',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
        },
        background: theme.palette.error.main,
        borderRadius: 20,
        opacity: '1 !important',
        position: 'absolute',
    },
}));

export const DSSwitch: React.FC<DSSwitchProps> = ({ offLabel = '', onLabel = '', ...props }) => {
    const classes = useDSSwitchStyles({
        offLabel,
        onLabel,
    });
    return <DSBasicSwitch classes={classes} color="default" {...props} />;
};
DSSwitch.displayName = 'DSSwitch';
